import { apiService } from '@/services/api'

export interface UploadedImage {
  id: string
  original_url: string
  thumbnail_url?: string
  small_url?: string
  medium_url?: string
  large_url?: string
  filename: string
  original_filename: string
  file_size: number
  width?: number
  height?: number
  alt_text?: string
  caption?: string
  usage_count: number
  folder?: string
  created_at: string
  updated_at: string
}

export class ImageUploadHandler {
  async uploadImage(file: File): Promise<string> {
    try {
      // Frontend validation for immediate user feedback
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image')
      }

      const maxSize = 10 * 1024 * 1024 // 10MB as per spec
      if (file.size > maxSize) {
        throw new Error('Image size must be less than 10MB')
      }

      const allowedFormats = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml']
      if (!allowedFormats.includes(file.type)) {
        throw new Error('Image format must be JPEG, PNG, WebP, GIF, or SVG')
      }

      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('file', file)

      // Upload through backend API
      const response = await apiService.blog.images.upload(formData)

      // Return the URL for the editor to use
      // Use medium_url for content, or fallback to original_url
      return response.medium_url || response.original_url
    } catch (error) {
      console.error('Image upload error:', error)
      throw error
    }
  }

  async uploadMultiple(files: File[]): Promise<UploadedImage[]> {
    try {
      // Validate all files first
      const maxBatchSize = 20
      if (files.length > maxBatchSize) {
        throw new Error(`Cannot upload more than ${maxBatchSize} files at once`)
      }

      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          throw new Error(`File "${file.name}" is not an image`)
        }

        const maxSize = 10 * 1024 * 1024
        if (file.size > maxSize) {
          throw new Error(`File "${file.name}" exceeds 10MB limit`)
        }
      }

      // Create FormData with multiple files
      const formData = new FormData()
      files.forEach(file => {
        formData.append('files', file)
      })

      // Upload through backend API
      const response = await apiService.blog.images.uploadMultiple(formData)
      return response
    } catch (error) {
      console.error('Multiple image upload error:', error)
      throw error
    }
  }

  async deleteImage(imageIdOrUrl: string): Promise<void> {
    try {
      const imageId = imageIdOrUrl

      // If it's a URL, extract the image ID from it
      // The backend should handle this, but we can try to extract ID if needed
      if (imageIdOrUrl.startsWith('http')) {
        // Try to extract ID from URL pattern
        // For now, pass the URL and let backend handle it
        // In future, we might want to store image IDs with the content
        console.warn('Deleting by URL is deprecated, use image ID instead')
        return
      }

      await apiService.blog.images.delete(imageId)
    } catch (error) {
      console.error('Image delete error:', error)
      throw error
    }
  }

  async getImageGallery(options?: {
    search?: string
    folder?: string
    has_usage?: boolean
    limit?: number
    offset?: number
  }): Promise<{ images: UploadedImage[]; total: number }> {
    try {
      const response = await apiService.blog.images.gallery(options)
      return response
    } catch (error) {
      console.error('Failed to fetch image gallery:', error)
      throw error
    }
  }

  async getImageUsage(imageId: string): Promise<{
    used_in_posts: Array<{ id: string; title: string; status: string }>
    total_usage: number
  }> {
    try {
      const response = await apiService.blog.images.getUsage(imageId)
      return response
    } catch (error) {
      console.error('Failed to fetch image usage:', error)
      throw error
    }
  }

  // Legacy method for backward compatibility - no longer needed
  async createBucketIfNotExists(): Promise<void> {
    // Backend handles all storage operations now
    // This method is kept for backward compatibility but does nothing
    console.log('Storage bucket management is handled by the backend')
  }
}

export const imageUploadHandler = new ImageUploadHandler()